// Flow definitions — spec chapter 6 §6.3–6.5, D6-1, D6-2. A flow is a variant of one
// canonical process: a graph of steps from the fixed step library, with guards from
// the expression language and detours. Validated at publish time.
import { z } from "zod";
import { EventType } from "../events/envelope.js";
import { parseExpr } from "./expr.js";
/** Canonical processes (chapter 4) and the events each may emit (R6.1). */
export const PROCESS_EVENTS = {
    receiving: ["received", "identified", "aggregated", "inspected", "status_changed", "observed", "moved"], // moved: received straight onto a storage location (owner 2026-11-02)
    put_away: ["moved", "aggregated", "disaggregated", "merged", "split"], // merged/split: bulk stock joins the row standing at the bin, or part of it goes (owner 2026-11-03) // a kolli put away onto an existing pallet (owner 2026-11-02)
    move: ["moved", "disaggregated", "split", "aggregated", "received", "emptied", "merged"], // a kolli moved off its pallet leaves the pallet first; part of loose stock is cut off before it moves; onto a pallet (aggregated), onto a new pallet issued in the aisle (received, the pallet's own birth) — owner 2026-11-02
    replenishment: ["moved"],
    aggregation: ["aggregated", "disaggregated", "identified"],
    split: ["split", "identified"],
    produce: ["produced", "identified"],
    counting: ["counted", "adjusted", "observed", "moved", "inspected"], // inspected: a container Trail does not know, reported to the office (counting step 3) // found-here: a container Trail had elsewhere is moved to the counted location (mockup counting-v1 §2)
    status: ["status_changed"],
    // A pick builds outbound pallets (packed, aggregated), takes containers for order lines
    // (picked), cuts pieces (split) and may lift a carton off a stock pallet (disaggregated).
    picking: ["picked", "split", "disaggregated", "aggregated", "packed", "identified", "counted", "moved"], // counted: the zero-point — the picker confirms the location is empty when its last container leaves
    packing: ["packed", "aggregated", "identified", "observed"],
    shipping: ["shipped", "observed"],
    direct_delivery: ["direct_delivery"],
    returns: ["received", "inspected", "status_changed"],
    inquiry: [],
};
export const ProcessName = z.enum(Object.keys(PROCESS_EVENTS));
export const AdvanceMode = z.enum(["auto", "confirm"]);
const Expr = z.string().min(1).superRefine((src, ctx) => {
    try {
        parseExpr(src);
    }
    catch (e) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `invalid expression: ${e.message}` });
    }
});
/** Where to go after a step: a step id, the end of the flow, or a guarded branch. */
export const Next = z.union([
    z.string().min(1),
    z.null(),
    z.object({
        branch: z.array(z.object({ when: Expr, to: z.string().min(1) })).min(1),
        else: z.string().min(1).nullable(),
    }),
]);
const common = {
    id: z.string().min(1),
    title: z.string().default(""),
    advance: AdvanceMode.default("auto"),
    next: Next.default(null),
};
/** The fixed step library (§6.4). Adding a type is a product release. */
export const StepDef = z.discriminatedUnion("type", [
    z.object({ ...common, type: z.literal("scan_location"), roles: z.array(z.string()).default([]), into: z.string().min(1) }),
    z.object({
        ...common,
        type: z.literal("scan_container"),
        expect: z.enum(["any", "logistic-unit", "trade-item"]).default("any"),
        into: z.string().min(1),
    }),
    z.object({
        ...common,
        type: z.literal("scan_many"),
        expect: z.enum(["any", "logistic-unit", "trade-item"]).default("trade-item"),
        into: z.string().min(1),
        /** Scans matching this kind end the step (e.g. the next pallet or the destination location). */
        terminateOn: z.enum(["location", "logistic-unit", "done-code"]).default("location"),
        terminatorInto: z.string().min(1).nullable().default(null),
        expectedCount: Expr.nullable().default(null),
    }),
    z.object({ ...common, type: z.literal("scan_product"), into: z.string().min(1) }),
    z.object({
        ...common,
        type: z.literal("capture_attribute"),
        attribute: z.enum(["lot", "bestBefore", "expiry", "productionDate", "serial", "netContent", "temperature"]),
        into: z.string().min(1),
        allowEntry: z.boolean().default(true),
    }),
    z.object({ ...common, type: z.literal("confirm_quantity"), proposed: Expr, into: z.string().min(1) }),
    z.object({ ...common, type: z.literal("choose"), options: z.array(z.object({ value: z.string(), label: z.string() })).min(1), into: z.string().min(1) }),
    z.object({ ...common, type: z.literal("show"), text: z.string() }),
    z.object({ ...common, type: z.literal("confirm"), text: z.string(), role: z.string().nullable().default(null) }),
    z.object({ ...common, type: z.literal("record_reason"), into: z.string().min(1) }),
    z.object({ ...common, type: z.literal("print_label"), template: z.string().min(1), subject: Expr, printerRole: z.string().min(1) }),
    z.object({ ...common, type: z.literal("propose"), strategy: z.enum(["put_away", "allocation"]), into: z.string().min(1) }),
    z.object({ ...common, type: z.literal("emit_event"), event: EventType, payload: z.record(Expr) }),
    z.object({ ...common, type: z.literal("detour"), flow: z.string().min(1), input: z.record(Expr).default({}), into: z.string().min(1).nullable().default(null) }),
    z.object({ ...common, type: z.literal("inquiry"), query: z.string().min(1), params: z.record(Expr).default({}), into: z.string().min(1) }),
    z.object({ ...common, type: z.literal("branch") }),
]);
export const FlowDefinition = z
    .object({
    id: z.string().min(1),
    version: z.number().int().positive(),
    name: z.string().min(1),
    process: ProcessName,
    start: z.string().min(1),
    steps: z.array(StepDef).min(1),
    note: z.string().default(""),
})
    .strict()
    .superRefine((flow, ctx) => {
    const ids = new Set();
    for (const s of flow.steps) {
        if (ids.has(s.id))
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate step id ${s.id}` });
        ids.add(s.id);
    }
    if (!ids.has(flow.start))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `start step ${flow.start} does not exist` });
    const allowed = new Set(PROCESS_EVENTS[flow.process]);
    for (const s of flow.steps) {
        const targets = [];
        if (typeof s.next === "string" || s.next === null)
            targets.push(s.next);
        else {
            targets.push(...s.next.branch.map((b) => b.to), s.next.else);
        }
        for (const t of targets) {
            if (t !== null && !ids.has(t))
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: `step ${s.id} points to unknown step ${t}` });
        }
        if (s.type === "emit_event" && !allowed.has(s.event)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `process ${flow.process} may not emit ${s.event}` });
        }
    }
});
/** Validate a definition for publishing (R6.1, R6.10). */
export function validateFlow(input) {
    return FlowDefinition.parse(input);
}
export function stepById(flow, id) {
    const s = flow.steps.find((x) => x.id === id);
    if (!s)
        throw new Error(`flow ${flow.id}@${flow.version}: no step ${id}`);
    return s;
}
//# sourceMappingURL=definition.js.map